import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, Card, CardContent, Grid, Typography } from "@mui/material";

const TRACK_BG = "#F1F1F1";
const PRIMARY_BLUE = "#396D87";
const MUTED = "#6B7280";
const BODY = "#2D2D2D";
const BORDER = "#EEF2F6";

/**
 * Attainment QTD, Key Metrics, and Breadth vs. Depth keep the same panel
 * height so they stay aligned regardless of how many items they render.
 */
const TWO_BAR_PANEL_HEIGHT = 152;

function AutoSyncedPill() {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        borderRadius: "999px",
        bgcolor: TRACK_BG,
        px: 1,
        py: 0.25,
      }}
    >
      <RefreshIcon sx={{ fontSize: 12, color: MUTED }} />
      <Typography
        component="span"
        sx={{ fontSize: 11, fontWeight: 600, color: MUTED, lineHeight: 1.2 }}
      >
        Auto-synced
      </Typography>
    </Box>
  );
}

function SectionLabel({ children, sx }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: MUTED,
        mb: 1,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

function NestedPanel({ children, sx }) {
  return (
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: "8px",
        p: 2,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function EmptyPanelMessage({ children }) {
  return (
    <Typography sx={{ fontSize: 12, color: MUTED, textAlign: "center" }}>
      {children}
    </Typography>
  );
}

/** Fixed-height panel whose content stays vertically centered. */
function TwoBarPanel({ label, children }) {
  return (
    <NestedPanel
      sx={{
        minHeight: TWO_BAR_PANEL_HEIGHT,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <SectionLabel>{label}</SectionLabel>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {children}
      </Box>
    </NestedPanel>
  );
}

function ProductSplitBar({ split }) {
  return (
    <Box sx={{ mb: 1.5, "&:last-child": { mb: 0 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.75,
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: BODY }}>
          {split.label}
        </Typography>
        <Typography sx={{ fontSize: 12, color: MUTED }}>
          {split.segments.map((s) => `${s.name} ${s.value}%`).join(" / ")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          height: 13,
          borderRadius: 999,
          overflow: "hidden",
          bgcolor: TRACK_BG,
        }}
      >
        {split.segments.map((s) => (
          <Box
            key={s.name}
            sx={{
              width: `${s.value}%`,
              height: "100%",
              bgcolor: s.color,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

function AttainmentBar({ product }) {
  const pct = product.goal
    ? Math.round((product.current / product.goal) * 100)
    : 0;
  const widthPct = Math.min(100, Math.max(0, pct));

  return (
    <Box sx={{ mb: 1.75, "&:last-child": { mb: 0 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.75,
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: BODY }}>
          {product.name}
        </Typography>
        <Typography sx={{ fontSize: 12, color: MUTED }}>
          {product.current} / {product.goal} ({pct}%)
        </Typography>
      </Box>
      <Box
        sx={{
          height: 13,
          borderRadius: 999,
          bgcolor: TRACK_BG,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${widthPct}%`,
            bgcolor: product.color || PRIMARY_BLUE,
            borderRadius: 999,
          }}
        />
      </Box>
    </Box>
  );
}

/**
 * Section 1 · Territory Overview
 * @param {{ overview: import("./qbrMockData").TerritoryOverview; onOverviewChange?: Function; readOnly?: boolean }} props
 */
export default function TerritoryOverviewCard({ overview }) {
  const attainmentProducts = overview.attainmentProducts ?? [];
  const productSplits = overview.productSplits ?? [];
  const breadthDepth = overview.breadthDepth ?? [];
  const topWriters = overview.topWriters ?? { columns: [], rows: [] };
  const competitive = overview.competitiveDynamics ?? {
    subtitle: "",
    claimants: [],
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "10px",
        border: `1px solid ${BORDER}`,
        boxShadow: "1px 4px 4px 0px #00000008",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2.5,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: BODY }}>
            Territory Overview
          </Typography>
          <AutoSyncedPill />
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={6}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TwoBarPanel label="Attainment QTD">
                {attainmentProducts.length > 0 ? (
                  attainmentProducts.map((product, index) => (
                    <AttainmentBar
                      key={product.name || index}
                      product={product}
                    />
                  ))
                ) : (
                  <EmptyPanelMessage>No insights found.</EmptyPanelMessage>
                )}
              </TwoBarPanel>

              <TwoBarPanel label="Key Metrics / Product Splits">
                {productSplits.length > 0 ? (
                  productSplits.map((split) => (
                    <ProductSplitBar key={split.label} split={split} />
                  ))
                ) : (
                  <EmptyPanelMessage>No insights found</EmptyPanelMessage>
                )}
              </TwoBarPanel>
              <NestedPanel sx={{ minHeight: 200 }}>
                <SectionLabel>Top Writers - Referrals</SectionLabel>
                {topWriters.columns.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 1.25,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: MUTED,
                      }}
                    >
                      HCP
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2.5, flexShrink: 0 }}>
                      {topWriters.columns.map((column) => (
                        <Typography
                          key={column.key}
                          sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                            color: MUTED,
                            minWidth: 64,
                            textAlign: "right",
                          }}
                        >
                          {column.label}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: 150,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <EmptyPanelMessage>No insights found</EmptyPanelMessage>
                  </Box>
                )}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
                >
                  {topWriters.rows.map((writer, index) => (
                    <Box
                      key={writer.name || index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            bgcolor: PRIMARY_BLUE,
                            color: "#fff",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: BODY,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {writer.name}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2.5,
                          flexShrink: 0,
                        }}
                      >
                        {topWriters.columns.map((column) => (
                          <Typography
                            key={column.key}
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: BODY,
                              minWidth: 64,
                              textAlign: "right",
                            }}
                          >
                            {writer.values?.[column.key] ?? ""}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </NestedPanel>
            </Box>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TwoBarPanel label="Breadth vs. Depth Summary">
                {breadthDepth.length > 0 ? (
                  <Grid container spacing={1}>
                    {breadthDepth.map((seg) => (
                      <Grid item xs={4} key={seg.label}>
                        <Box sx={{ textAlign: "center" }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 4,
                              borderRadius: 999,
                              bgcolor: seg.color,
                              mx: "auto",
                              mb: 0.75,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: "3rem",
                              fontWeight: 700,
                              color: seg.color,
                              lineHeight: 1.1,
                            }}
                          >
                            {seg.count}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.5,
                              fontSize: 12,
                              fontWeight: 600,
                              color: BODY,
                            }}
                          >
                            {seg.label}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: MUTED }}>
                            {seg.definition}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <EmptyPanelMessage>No insights found</EmptyPanelMessage>
                )}
              </TwoBarPanel>
              <NestedPanel sx={{ maxHeight: 402 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <SectionLabel sx={{ mb: 0 }}>
                    Competitive Dynamics
                  </SectionLabel>
                  {competitive.subtitle ? (
                    <Typography sx={{ fontSize: 12, color: MUTED }}>
                      {competitive.subtitle}
                    </Typography>
                  ) : null}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                    minHeight: "376px",
                    overflow: "auto",
                  }}
                >
                  {competitive.claimants.length === 0 ? (
                    <Box
                      flex={1}
                      display="flex"
                      justifyContent="center"
                      flexDirection="column"
                      height="100px"
                    >
                      <EmptyPanelMessage>No insights found</EmptyPanelMessage>
                    </Box>
                  ) : (
                    competitive.claimants.slice(0, 5).map((claimant) => (
                      <Box
                        key={claimant.name}
                        sx={{
                          border: `1px solid ${BORDER}`,
                          borderRadius: "8px",
                          p: 1.5,
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: BODY,
                            }}
                          >
                            {claimant.name}
                          </Typography>
                          <Typography
                            sx={{ mt: 0.5, fontSize: 12, color: MUTED }}
                          >
                            {claimant.note}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 22,
                              fontWeight: 700,
                              color: PRIMARY_BLUE,
                              lineHeight: 1,
                            }}
                          >
                            {claimant.claims}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.25,
                              fontSize: 10,
                              fontWeight: 600,
                              letterSpacing: "0.06em",
                              textTransform: "uppercase",
                              color: MUTED,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Active Patients
                          </Typography>
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              </NestedPanel>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
